import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Material } from '../../types';
import { materialFormFields } from '../../config/formFields';

interface MaterialesSectionProps {
  data: {
    materiales: Material[];
  };
  operations: {
    handleMaterialSubmit: (data: Record<string, any>) => Promise<void>;
    handleMaterialDelete: (item: Material) => Promise<void>;
    handleMaterialEdit: (item: Material, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const MaterialesSection: React.FC<MaterialesSectionProps> = ({ data, operations, userPermissions }) => {
  // Table column configurations
  const materialColumns: TableColumn<Material>[] = [
    {
      key: 'codigoMarbar',
      title: 'Código Marbar',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleMaterialEdit
    },
    {
      key: 'nombreComercial',
      title: 'Nombre Comercial',
      width: '25%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleMaterialEdit
    },
    {
      key: 'categoria',
      title: 'Categoría',
      width: '15%',
      editable: true,
      editType: 'select',
      selectOptions: [
        { value: 'densificante', label: 'Densificante' },
        { value: 'viscosificante', label: 'Viscosificante' },
        { value: 'fluido', label: 'Fluido' },
        { value: 'aditivo', label: 'Aditivo' },
        { value: 'sal', label: 'Sal' }
      ],
      onEdit: operations.handleMaterialEdit
    },
    {
      key: 'unidad',
      title: 'Unidad',
      width: '10%',
      editable: true,
      editType: 'select',
      selectOptions: [
        { value: 'kg', label: 'kg' },
        { value: 'l', label: 'l' },
        { value: 'm3', label: 'm³' },
        { value: 'día', label: 'día' },
        { value: 'serv', label: 'serv' },
        { value: 'uu', label: 'uu' }
      ],
      onEdit: operations.handleMaterialEdit
    },
    {
      key: 'costoUnitario',
      title: 'Costo Unitario',
      width: '15%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleMaterialEdit,
      render: (value) => value ? `$${value}` : '$0'
    },
    {
      key: 'activo',
      title: 'Estado',
      width: '10%',
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold',
          backgroundColor: value ? '#e8f5e8' : '#ffebee',
          color: value ? '#2e7d32' : '#c62828'
        }}>
          {value ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];

  // Quick Setup Handler for standard materials
  const handleMaterialesQuickSetup = async () => {
    if (window.confirm('¿Cargar materiales estándar? Esta acción creará los materiales más comunes en control de sólidos.')) {
      const materialTemplates = [
        { codigoMarbar: 'BAR-001', nombreComercial: 'Baritina API', categoria: 'densificante', unidad: 'kg', costoUnitario: 2.5, activo: true },
        { codigoMarbar: 'BEN-001', nombreComercial: 'Bentonita Wyoming', categoria: 'viscosificante', unidad: 'kg', costoUnitario: 1.8, activo: true },
        { codigoMarbar: 'CAR-001', nombreComercial: 'Carbonato de Calcio', categoria: 'aditivo', unidad: 'kg', costoUnitario: 1.2, activo: true },
        { codigoMarbar: 'PAC-001', nombreComercial: 'PAC-L', categoria: 'viscosificante', unidad: 'kg', costoUnitario: 4.5, activo: true },
        { codigoMarbar: 'PAC-002', nombreComercial: 'PAC-R', categoria: 'viscosificante', unidad: 'kg', costoUnitario: 4.8, activo: true },
        { codigoMarbar: 'XAN-001', nombreComercial: 'Goma Xantan', categoria: 'viscosificante', unidad: 'kg', costoUnitario: 6.2, activo: true },
        { codigoMarbar: 'SOD-001', nombreComercial: 'Soda Ash', categoria: 'aditivo', unidad: 'kg', costoUnitario: 0.8, activo: true },
        { codigoMarbar: 'POT-001', nombreComercial: 'Potasa Cáustica', categoria: 'aditivo', unidad: 'kg', costoUnitario: 1.5, activo: true },
        { codigoMarbar: 'LIG-001', nombreComercial: 'Lignito', categoria: 'aditivo', unidad: 'kg', costoUnitario: 2.1, activo: true },
        { codigoMarbar: 'DIE-001', nombreComercial: 'Diesel', categoria: 'fluido', unidad: 'l', costoUnitario: 1.2, activo: true }
      ];

      try {
        for (const template of materialTemplates) {
          await operations.handleMaterialSubmit(template);
        }
        alert('✅ Materiales estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando materiales:', error);
        alert('❌ Error cargando materiales. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🧪 Gestión de Materiales</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra los materiales y aditivos utilizados en las operaciones de control de sólidos
        </p>
      </div>

      {/* Materiales Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Materiales y Aditivos',
          '🧪',
          data.materiales,
          materialFormFields,
          materialColumns,
          operations.handleMaterialSubmit,
          async () => {}, // Materiales don't have toggle functionality
          operations.handleMaterialDelete,
          {
            description: 'Gestiona los materiales, aditivos y químicos utilizados en las operaciones de perforación y control de sólidos',
            quickSetup: {
              title: 'Carga Rápida de Materiales',
              description: 'Crea materiales estándar (Baritina, Bentonita, Polímeros, Aditivos, etc.)',
              buttonLabel: '🧪 Cargar Materiales Estándar',
              onQuickSetup: handleMaterialesQuickSetup
            }
          }
        )}
      />

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e3f2fd',
        borderRadius: '12px',
        border: '2px solid #2196F3'
      }}>
        <h3 style={{ color: '#1565C0', marginBottom: '15px' }}>📊 Resumen de Materiales</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🧪 Total Materiales
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              {data.materiales.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ✅ Materiales Activos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.materiales.filter(material => material.activo).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🏷️ Categorías
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {new Set(data.materiales.map(material => material.categoria)).size}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              💰 Costo Promedio
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#9C27B0'
            }}>
              {data.materiales.length > 0
                ? Math.round(data.materiales.reduce((sum, material) => sum + (material.costoUnitario || 0), 0) / data.materiales.length)
                : 0
              }
            </div>
          </div>
        </div>

        {/* Materiales por Categoría */}
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ color: '#1565C0', marginBottom: '10px' }}>Materiales por Categoría:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
            {['densificante', 'viscosificante', 'fluido', 'aditivo', 'sal'].map(categoria => {
              const categoriaMateriales = data.materiales.filter(material => material.categoria === categoria);
              if (categoriaMateriales.length === 0) return null;
              return (
                <div key={categoria} style={{
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #ddd'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1565C0' }}>
                    🧪 {categoria.charAt(0).toUpperCase() + categoria.slice(1)}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {categoriaMateriales.map(material => (
                      <div key={material.id} style={{
                        marginBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>{material.nombreComercial}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#666', fontSize: '12px' }}>
                            ${material.costoUnitario}/{material.unidad}
                          </span>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            backgroundColor: material.activo ? '#e8f5e8' : '#ffebee',
                            color: material.activo ? '#2e7d32' : '#c62828'
                          }}>
                            {material.activo ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '12px', color: '#2196F3', marginTop: '8px', fontWeight: 'bold' }}>
                    {categoriaMateriales.length} material{categoriaMateriales.length !== 1 ? 'es' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Materiales con Mayor Costo */}
        {data.materiales.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#1565C0', marginBottom: '10px' }}>Materiales de Mayor Costo:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
              {data.materiales
                .sort((a, b) => (b.costoUnitario || 0) - (a.costoUnitario || 0))
                .slice(0, 6)
                .map(material => (
                  <div key={material.id} style={{
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1565C0' }}>
                      🧪 {material.nombreComercial}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      {material.categoria} | {material.codigoMarbar}
                    </div>
                    <div style={{ fontSize: '18px', color: '#2196F3', fontWeight: 'bold' }}>
                      ${material.costoUnitario} / {material.unidad}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {data.materiales.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay materiales registrados. Usa la carga rápida para empezar con materiales estándar.
          </div>
        )}
      </div>
    </>
  );
};